import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProTopBar } from '../components/ProTopBar';
import { ProMetricStrip, ProMetric } from '../components/ProMetric';
import { PatientSearchBar } from '../components/PatientSearchBar';
import { PatientTableHeader } from '../components/PatientTableHeader';
import { PatientRow } from '../components/PatientRow';
import { PatientDetailPanel } from '../components/PatientDetailPanel';
import { PatientsEmptyState } from '../components/PatientsEmptyState';
import { usePatients, useCreatePatient } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { ApiError, isPaymentRequired, limitReason, type LimitReason } from '../lib/api';
import { PaywallNotice } from '../components/PaywallNotice';
import { LimitReachedModal } from '../components/LimitReachedModal';
import type { PatientListItem } from '../lib/types';
import styles from './Clienti.module.css';

function daysSince(iso: string): number {
  const then = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((now - then) / 86400000);
}

export default function Clienti() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: patients, isLoading, isError, error } = usePatients();
  const createMut = useCreatePatient();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', email: '', phone: '' });
  const [newError, setNewError] = useState<string | null>(null);
  const [limit, setLimit] = useState<LimitReason | null>(null);

  // Deep-link `?new=1` (es. CTA "Nuovo paziente" dalla Dashboard) → apre il modale
  // di creazione e pulisce il query param per evitare riaperture su refresh.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setShowNewModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreate = () => {
    setNewError(null);
    if (!newForm.name.trim() || !newForm.email.trim()) {
      setNewError('Nome e email sono obbligatori');
      return;
    }
    createMut.mutate(
      { name: newForm.name.trim(), email: newForm.email.trim(), phone: newForm.phone.trim() || null },
      {
        onSuccess: (res) => {
          setShowNewModal(false);
          setNewForm({ name: '', email: '', phone: '' });
          setSelectedId(res.patient.id);
        },
        onError: (err) => {
          // Cap pazienti del piano → modale upgrade, non errore grezzo nel form.
          const reason = limitReason(err);
          if (reason) { setShowNewModal(false); setLimit(reason); return; }
          setNewError(err instanceof ApiError ? err.message : err.message || String(err));
        },
      }
    );
  };

  const filtered = useMemo<PatientListItem[]>(() => {
    if (!patients) return [];
    const q = query.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.human_id?.toLowerCase().includes(q) ?? false) ||
        (p.email?.toLowerCase().includes(q) ?? false)
    );
  }, [patients, query]);

  const selected = useMemo(
    () => filtered.find((p) => p.id === selectedId) || filtered[0] || null,
    [filtered, selectedId]
  );

  // Aggregate metrics dai dati reali
  const metrics = useMemo(() => {
    const list = patients || [];
    const total = list.length;
    const active = list.filter(
      (p) => p.last_check && daysSince(p.last_check) <= 7
    ).length;
    const attention = list.filter(
      (p) =>
        p.last_check &&
        daysSince(p.last_check) > 7 &&
        daysSince(p.last_check) <= 14
    ).length;
    const totalMods = list.reduce((sum, p) => sum + (p.mods_7d || 0), 0);
    return { total, active, attention, totalMods };
  }, [patients]);

  const breadcrumb = isLoading
    ? 'Studio Pro · Caricamento…'
    : `Studio Pro · ${metrics.total} ${
        metrics.total === 1 ? 'cliente attivo' : 'clienti attivi'
      }`;

  if (isError) {
    return (
      <>
        <ProTopBar breadcrumb="Studio Pro · Errore" title="Clienti" />
        {isPaymentRequired(error) ? (
          <PaywallNotice resource="la gestione clienti" />
        ) : (
          <div className={styles.errorBox}>
            Errore caricamento clienti. Ricarica la pagina o controlla la tua connessione.
          </div>
        )}
      </>
    );
  }

  const showEmpty = !isLoading && (patients?.length ?? 0) === 0;

  return (
    <>
      {limit && <LimitReachedModal reason={limit} onClose={() => setLimit(null)} />}
      <ProTopBar
        breadcrumb={breadcrumb}
        title="Clienti"
        ctaLabel="Nuovo cliente"
        onCta={() => { setNewError(null); setShowNewModal(true); }}
      />

      <ProMetricStrip>
        <ProMetric eyebrow="Attivi" value={metrics.active} sub={`Su ${metrics.total} totali`} />
        <ProMetric
          eyebrow="Da rivedere"
          value={metrics.attention}
          sub="Attenzione"
          variant={metrics.attention > 0 ? 'orange' : 'default'}
        />
        <ProMetric eyebrow="Aderenza media" value="—" sub="Phase 4 backend" variant="green" />
        <ProMetric eyebrow="Modifiche 7g" value={metrics.totalMods} sub="Totale" />
      </ProMetricStrip>

      {showEmpty ? (
        <PatientsEmptyState proCode={user?.pro_code || null} />
      ) : (
        <div className={styles.layout}>
          <div className={styles.tableCol}>
            <PatientSearchBar
              query={query}
              onQueryChange={setQuery}
              onFiltri={() => alert('Filtri avanzati — Sprint 3')}
              resultCount={filtered.length}
            />
            <PatientTableHeader />
            <div className={styles.rows}>
              {isLoading ? (
                <div className={styles.loading}>Caricamento…</div>
              ) : filtered.length === 0 ? (
                <div className={styles.noResults}>
                  Nessun cliente trovato per "{query}".
                </div>
              ) : (
                filtered.map((p) => (
                  <PatientRow
                    key={p.id}
                    patient={p}
                    selected={selected?.id === p.id}
                    onSelect={(pat) => setSelectedId(pat.id)}
                  />
                ))
              )}
            </div>
          </div>
          <PatientDetailPanel
            patient={selected}
            onOpenPlan={(p) => navigate(`/piani?patient=${encodeURIComponent(p.id)}`)}
          />
        </div>
      )}

      {showNewModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-patient-title"
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,20,16,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={() => !createMut.isPending && setShowNewModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--paper, #fff)', borderRadius: 12, padding: 28, width: 440, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}
          >
            <h2 id="new-patient-title" style={{ marginTop: 0, marginBottom: 6, fontFamily: 'var(--display)', fontSize: 22, fontWeight: 800 }}>
              Nuovo cliente
            </h2>
            <div style={{ color: 'var(--mute)', fontSize: 13, marginBottom: 20 }}>
              Crea l'account paziente. Il paziente accederà via magic-link su <code>dietamenu.schedy.app</code>.
            </div>
            {newError && (
              <div role="alert" style={{ background: '#fff0f0', border: '1px solid var(--red, #c0392b)', borderRadius: 8, padding: 10, fontSize: 13, color: 'var(--red, #c0392b)', marginBottom: 14 }}>
                {newError}
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Nome*</label>
            <input
              type="text"
              value={newForm.name}
              onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
              placeholder="es. Maria Bianchi"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, marginBottom: 14 }}
              disabled={createMut.isPending}
            />
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Email*</label>
            <input
              type="email"
              value={newForm.email}
              onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
              placeholder="maria.bianchi@email.com"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, marginBottom: 14 }}
              disabled={createMut.isPending}
            />
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Telefono (opzionale)</label>
            <input
              type="tel"
              value={newForm.phone}
              onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })}
              placeholder="+39..."
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, marginBottom: 22 }}
              disabled={createMut.isPending}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowNewModal(false)}
                disabled={createMut.isPending}
                style={{ padding: '9px 16px', borderRadius: 999, border: '1px solid var(--line)', background: 'transparent', cursor: 'pointer', fontWeight: 600 }}
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={createMut.isPending}
                style={{ padding: '9px 18px', borderRadius: 999, border: 'none', background: 'var(--green-deep, #0b8a3e)', color: '#fff', cursor: 'pointer', fontWeight: 700, opacity: createMut.isPending ? 0.6 : 1 }}
              >
                {createMut.isPending ? 'Creazione…' : 'Crea cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
